import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useOSettings } from './use-settings';
import { authenticatedSettingsLoadPromise } from './use-settings-load-when-authenticated';
import { settingsLoadPromise } from './use-update-settings';
import { useOUserSettingsStore } from '@/stores/use-user-settings-store';
import { shouldShowMaintenancePage } from '@/utils/maintenance-mode';
import {
  clearMaintenanceRedirect,
  getMaintenanceRedirect,
  getQueryRedirect,
  isMaintenancePath,
  isValidMaintenanceRedirect,
  resolveMaintenanceRedirect,
  saveMaintenanceRedirect,
} from '@/utils/maintenance-redirect';

const MAINTENANCE_ROUTE_NAME = 'maintenance';

/**
 * Re-evaluates maintenance mode when config settings or user roles change,
 * redirecting to or away from the maintenance page as needed.
 *
 * The original destination is stored in `query.redirect` and sessionStorage so the
 * user is returned to the correct page once maintenance ends, including after reload.
 */
export function useMaintenanceRedirect() {
  const router = useRouter();
  const route = useRoute();
  const { maintenance_mode, maintenance_mode_exclude_roles } = useOSettings();
  const { oUserSettings } = storeToRefs(useOUserSettingsStore());

  const showMaintenance = computed(() =>
    shouldShowMaintenancePage(oUserSettings.value, {
      maintenance_mode: maintenance_mode?.value,
      maintenance_mode_exclude_roles: maintenance_mode_exclude_roles?.value,
    }),
  );

  watch(
    [showMaintenance, () => oUserSettings.value?.key, () => route.name, () => route.meta.authenticate],
    async ([shouldRedirect]) => {
      // Wait for the router to resolve the browser URL before reading route state.
      // Without this, a reload on /maintenance can briefly see route "/" and overwrite redirect.
      await router.isReady();
      await settingsLoadPromise;
      await authenticatedSettingsLoadPromise;

      if (route.name === MAINTENANCE_ROUTE_NAME) {
        if (shouldRedirect) {
          const fromQuery = getQueryRedirect(route.query.redirect);

          if (fromQuery) {
            // URL already has a redirect — keep it unchanged on reload.
            saveMaintenanceRedirect(fromQuery);
            return;
          }

          const stored = getMaintenanceRedirect();
          if (stored) {
            router.replace({ name: MAINTENANCE_ROUTE_NAME, query: { redirect: stored } });
          }

          return;
        }

        const redirect = resolveMaintenanceRedirect(route.query.redirect);
        clearMaintenanceRedirect();
        router.replace(redirect ? { path: redirect } : { path: '/' });
        return;
      }

      if (route.meta.authenticate === false) {
        return;
      }

      if (shouldRedirect && !isMaintenancePath(route.path) && isValidMaintenanceRedirect(route.fullPath)) {
        saveMaintenanceRedirect(route.fullPath);
        router.replace({ name: MAINTENANCE_ROUTE_NAME, query: { redirect: route.fullPath } });
      }
    },
    { immediate: true, flush: 'post' },
  );
}
